/**
 * Paths to JSON Converter for Social Proof
 *
 * Converts arrays of social proof paths to full entity objects.
 * Used for preparing hydrated data for AI server submission.
 */

import type { Review, Rating, StudyCase, BigNumber } from '@segmently/schema-models/entities';
import type { SocialProofHydratedData, LoadedSocialProofData } from './types';

/**
 * Extracts entity IDs from paths for a given prefix
 */
function extractIds(paths: string[], prefix: string): Set<string> {
  const ids = new Set<string>();
  const regex = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\[([^\\]]+)\\]`);
  for (const path of paths) {
    const match = path.match(regex);
    if (match) {
      ids.add(match[1]);
    }
  }
  return ids;
}

/**
 * Converts social proof paths to hydrated entity objects
 *
 * @param paths - Array of social proof paths
 * @param data - Loaded social proof entities from Firestore
 * @returns Hydrated data with resolved entities
 */
export function pathsToJson(
  paths: string[],
  data: LoadedSocialProofData,
): SocialProofHydratedData {
  const reviewIds = extractIds(paths, '@review');
  const ratingIds = extractIds(paths, '@rating');
  const studyCaseIds = extractIds(paths, '@studyCase');
  const bigNumberIds = extractIds(paths, '@bigNumber');

  return {
    reviews: data.reviews.filter((r) => reviewIds.has(r.id)),
    ratings: data.ratings.filter((r) => ratingIds.has(r.id)),
    studyCases: data.studyCases.filter((sc) => studyCaseIds.has(sc.id)),
    bigNumbers: data.bigNumbers.filter((bn) => bigNumberIds.has(bn.id)),
  };
}
